// Group org form instances by template for the Fulfillment → Forms recent list.

import type { OrgFormInstanceListItem } from "./orgFormInstanceTypes.js";

export type OrgFormInstanceGroup = {
  templateId: string;
  displayName: string;
  instances: OrgFormInstanceListItem[];
  latest: OrgFormInstanceListItem;
  older: OrgFormInstanceListItem[];
};

export const DEFAULT_RECENT_FORM_GROUPS_PAGE_SIZE = 5;

function instanceSortTime(inst: OrgFormInstanceListItem): number {
  const updated = Date.parse(inst.updated_at);
  if (Number.isFinite(updated)) return updated;
  const created = Date.parse(inst.created_at);
  return Number.isFinite(created) ? created : 0;
}

function sortInstancesNewestFirst(
  instances: OrgFormInstanceListItem[],
): OrgFormInstanceListItem[] {
  return [...instances].sort((a, b) => {
    const diff = instanceSortTime(b) - instanceSortTime(a);
    if (diff !== 0) return diff;
    return b.id.localeCompare(a.id);
  });
}

export function groupOrgFormInstancesByTemplate(
  instances: OrgFormInstanceListItem[],
): OrgFormInstanceGroup[] {
  const byTemplate = new Map<string, OrgFormInstanceListItem[]>();
  for (const inst of instances) {
    const key = inst.form_template_id;
    const list = byTemplate.get(key) ?? [];
    list.push(inst);
    byTemplate.set(key, list);
  }

  const groups: OrgFormInstanceGroup[] = [];
  for (const [templateId, list] of byTemplate) {
    const sorted = sortInstancesNewestFirst(list);
    const latest = sorted[0];
    if (!latest) continue;
    groups.push({
      templateId,
      displayName: latest.template_display_name || latest.template_form_key || templateId,
      instances: sorted,
      latest,
      older: sorted.slice(1),
    });
  }

  groups.sort((a, b) => instanceSortTime(b.latest) - instanceSortTime(a.latest));
  return groups;
}

export function paginateOrgFormGroups(
  groups: OrgFormInstanceGroup[],
  pageSize = DEFAULT_RECENT_FORM_GROUPS_PAGE_SIZE,
  showAll = false,
): { visible: OrgFormInstanceGroup[]; totalCount: number; hasMore: boolean } {
  const totalCount = groups.length;
  if (showAll) {
    return { visible: groups, totalCount, hasMore: totalCount > pageSize };
  }
  return {
    visible: groups.slice(0, pageSize),
    totalCount,
    hasMore: totalCount > pageSize,
  };
}

export function canDeleteSupersededOrgFormInstance(args: {
  instance: OrgFormInstanceListItem;
  group: OrgFormInstanceGroup;
  isAdmin: boolean;
}): boolean {
  if (!args.isAdmin) return false;
  if (args.instance.status !== "draft") return false;
  return args.instance.id !== args.group.latest.id;
}
